import { z } from 'zod';
import { vocabularyCodeSchema } from './vocabulary.js';
/**
 * **Ce qu'une situation vaut pour un client** — le tableau des non-conformités par marché de GHP
 * (12/10/2026, #413). La même situation n'a pas le même effet partout : une collecte non séparative
 * est une dérogation à Nantes, une non-conformité dans les Mauges, sans objet à Noirmoutier.
 *
 * - `anomaly` — « Non conforme » : une anomalie, qui compte dans la conclusion ;
 * - `accepted` — « Conforme » : rien à relever ;
 * - `derogation` — « Dérogation » : pas d'anomalie, mais la mention s'imprime au rapport ;
 * - `not_applicable` — « Sans objet » : la situation ne se pose pas pour ce client.
 *
 * ⚠️ **Pas `compliant` / `non_compliant`** : ces deux mots sont déjà les valeurs de la conclusion
 * (`InspectionResult`), où ils disent « raccordé » et « non raccordé ». Les reprendre ici ferait lire
 * une conclusion dans l'effet d'une seule situation.
 */
export const ANOMALY_EFFECTS = ['anomaly', 'accepted', 'derogation', 'not_applicable'];
export const anomalyEffectSchema = z.enum(ANOMALY_EFFECTS);
/** Les mots du tableau de GHP — des libellés imprimés (troisième exception du glossaire). */
export const ANOMALY_EFFECT_LABELS = {
    anomaly: 'Non conforme',
    accepted: 'Conforme',
    derogation: 'Dérogation',
    not_applicable: 'Sans objet',
};
/**
 * La règle d'un client pour **une** situation — un code de la liste `technical` (M17).
 *
 * - `recommendation` — la phrase de préconisation que le rapport imprime avec la situation ;
 * - `note` — la case de GHP telle qu'il l'a écrite (« Si couverte + bordures : conforme »). Elle ne
 *   décide de rien et ne s'imprime pas ;
 * - `condition` — la question que la tablette pose à l'agent quand la règle dépend de ce qu'il voit
 *   sur place, et `effectIfYes` l'effet de son « oui » ; `effect` est alors celui du « non »
 *   (`effectFor`). ⚠️ `nullish` : une tournée en cache d'avant les questions n'en porte pas.
 */
export const clientAnomalyEffectSchema = z
    .object({
    code: vocabularyCodeSchema,
    effect: anomalyEffectSchema,
    recommendation: z.string().trim().min(1).nullable(),
    note: z.string().trim().min(1).nullable(),
    condition: z.string().trim().min(1).nullish(),
    effectIfYes: anomalyEffectSchema.nullish(),
})
    .refine((rule) => (rule.condition == null) === (rule.effectIfYes == null), {
    error: 'une question va avec l’effet de son « oui », et réciproquement',
});
/**
 * L'anomalie compte-t-elle dans la conclusion ? Oui sans effet — pas de règle pour son client, la
 * gravité du vocabulaire s'applique comme avant —, et oui quand le client la tient pour une
 * non-conformité.
 */
export function countsInConclusion(anomaly) {
    return anomaly.effect == null || anomaly.effect === 'anomaly';
}
/**
 * L'effet que la règle donne **d'après la réponse de l'agent** (#413) : celui du « oui » ou du
 * « non » pour une règle conditionnelle, le sien pour une autre.
 *
 * ⚠️ **Sans réponse, la situation compte comme une anomalie** — la règle d'avant les questions, qui
 * reste celle d'une case qu'on n'a pas su trancher : un doute non levé n'est pas une conformité.
 */
export function effectFor(rule, conditionMet) {
    if (rule.condition == null || rule.effectIfYes == null)
        return rule.effect;
    if (conditionMet === null)
        return 'anomaly';
    return conditionMet ? rule.effectIfYes : rule.effect;
}
//# sourceMappingURL=anomaly-effect.js.map